(() => {
  'use strict';

  if (customElements.get('ohcrisp-shoppable-gallery')) return;

  const openGalleries = new Set();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'select:not([disabled])',
    'input:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  class OhCrispShoppableGallery extends HTMLElement {
    connectedCallback() {
      this.destroy();

      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.rail = this.querySelector('[data-smg-rail]');
      this.cards = Array.from(this.querySelectorAll('[data-smg-card]:not(.smg__card--placeholder)'));
      this.dialog = this.querySelector('[data-smg-dialog]');
      this.fallbackBackdrop = this.querySelector('[data-smg-fallback-backdrop]');
      this.mediaStage = this.querySelector('[data-smg-media-stage]');
      this.productMount = this.querySelector('[data-smg-product-mount]');
      this.counter = this.querySelector('[data-smg-counter]');
      this.announcer = this.querySelector('[data-smg-announcer]');
      this.badge = this.querySelector('[data-smg-modal-badge]');
      this.creator = this.querySelector('[data-smg-modal-creator]');
      this.caption = this.querySelector('[data-smg-modal-caption]');
      this.originalLink = this.querySelector('[data-smg-modal-original]');
      this.viewer = this.querySelector('[data-smg-viewer]');
      this.playButton = this.querySelector('[data-smg-play]');
      this.videoControls = this.querySelector('[data-smg-video-controls]');
      this.playToggle = this.querySelector('[data-smg-play-toggle]');
      this.muteToggle = this.querySelector('[data-smg-mute-toggle]');
      this.railPrev = this.querySelector('[data-smg-rail-prev]');
      this.railNext = this.querySelector('[data-smg-rail-next]');
      this.modalPrev = this.querySelector('[data-smg-modal-prev]');
      this.modalNext = this.querySelector('[data-smg-modal-next]');
      this.closeButton = this.querySelector('[data-smg-close]');
      this.loop = this.dataset.loop === 'true';
      this.currentIndex = 0;
      this.activeVideo = null;
      this.returnFocus = null;
      this.restoreFocusOnClose = true;
      this.isOpen = false;
      this.dragState = null;
      this.touchTapState = null;
      this.pendingTouchTap = null;
      this.touchOpenTimer = 0;
      this.swipeState = null;
      this.suppressNextClick = false;
      this.suppressClickTimer = 0;
      this.scrollFrame = 0;
      this.productController = null;
      this.fallbackSection = null;

      if (!this.rail || !this.dialog) return;

      this.bindEvents();
      this.initPosterObserver();
      this.initRailResizeObserver();
      this.updateRailButtons();
      this.updateModalButtons();
    }

    disconnectedCallback() {
      this.destroy();
    }

    destroy() {
      if (this.controller) this.controller.abort();
      if (this.posterObserver) this.posterObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.productController) this.productController.abort();
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      if (this.suppressClickTimer) window.clearTimeout(this.suppressClickTimer);
      if (this.touchOpenTimer) window.clearTimeout(this.touchOpenTimer);
      this.stopActiveVideo();
      this.releaseBodyLock();

      if (this.dialog && (this.dialog.open || this.dialog.hasAttribute('open'))) {
        if (typeof this.dialog.close === 'function') {
          try {
            this.dialog.close();
          } catch (_error) {
            this.dialog.removeAttribute('open');
          }
        } else {
          this.dialog.removeAttribute('open');
        }
      }
      this.hideDialogFallback();

      this.controller = null;
      this.productController = null;
      this.posterObserver = null;
      this.resizeObserver = null;
      this.scrollFrame = 0;
      this.dragState = null;
      this.touchTapState = null;
      this.pendingTouchTap = null;
      this.touchOpenTimer = 0;
      this.swipeState = null;
      this.suppressNextClick = false;
      this.suppressClickTimer = 0;
      this.fallbackSection = null;
    }

    bindEvents() {
      const options = { signal: this.signal };

      this.querySelectorAll('[data-smg-open]').forEach((button, index) => {
        button.addEventListener('click', (event) => {
          if (this.pendingTouchTap?.opener === button) this.clearPendingTouchOpen();
          if (this.suppressNextClick) {
            event.preventDefault();
            this.suppressNextClick = false;
            return;
          }
          this.open(index, button);
        }, options);
      });

      if (this.railPrev) this.railPrev.addEventListener('click', () => this.scrollRail(-1), options);
      if (this.railNext) this.railNext.addEventListener('click', () => this.scrollRail(1), options);
      if (this.modalPrev) this.modalPrev.addEventListener('click', () => this.showPrevious(), options);
      if (this.modalNext) this.modalNext.addEventListener('click', () => this.showNext(), options);
      if (this.closeButton) this.closeButton.addEventListener('click', () => this.close(true), options);
      if (this.playButton) this.playButton.addEventListener('click', () => this.togglePlay(), options);
      if (this.playToggle) this.playToggle.addEventListener('click', () => this.togglePlay(), options);
      if (this.muteToggle) this.muteToggle.addEventListener('click', () => this.toggleMute(), options);

      this.rail.addEventListener('scroll', () => {
        this.scheduleRailUpdate();
      }, { passive: true, signal: this.signal });
      window.addEventListener('resize', () => this.scheduleRailUpdate(), {
        passive: true,
        signal: this.signal,
      });

      this.rail.addEventListener('keydown', (event) => this.handleRailKeydown(event), options);
      this.rail.addEventListener('pointerdown', (event) => this.startRailDrag(event), options);
      this.rail.addEventListener('pointermove', (event) => this.moveRailDrag(event), options);
      this.rail.addEventListener('pointerup', (event) => this.endRailDrag(event), options);
      this.rail.addEventListener('pointercancel', (event) => this.endRailDrag(event), options);
      this.rail.addEventListener('lostpointercapture', (event) => this.endRailDrag(event), options);

      if (!('PointerEvent' in window)) {
        this.rail.addEventListener('touchstart', (event) => this.startRailTouch(event), {
          passive: true,
          signal: this.signal,
        });
        this.rail.addEventListener('touchmove', (event) => this.moveRailTouch(event), {
          passive: true,
          signal: this.signal,
        });
        this.rail.addEventListener('touchend', (event) => this.endRailTouch(event), {
          passive: true,
          signal: this.signal,
        });
        this.rail.addEventListener('touchcancel', () => {
          this.touchTapState = null;
        }, {
          passive: true,
          signal: this.signal,
        });
      }

      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close(true);
      }, options);
      this.dialog.addEventListener('close', () => this.finalizeClose(), options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close(true);
      }, options);
      this.dialog.addEventListener('keydown', (event) => this.handleDialogKeydown(event), options);
      if (this.fallbackBackdrop) {
        this.fallbackBackdrop.addEventListener('click', () => this.close(true), options);
      }

      if (this.viewer) {
        this.viewer.addEventListener('pointerdown', (event) => this.startViewerSwipe(event), options);
        this.viewer.addEventListener('pointerup', (event) => this.endViewerSwipe(event), options);
        this.viewer.addEventListener('pointercancel', () => {
          this.swipeState = null;
        }, options);
      }

      document.addEventListener('ohcrisp:gallery-opening', (event) => {
        if (event.detail?.gallery !== this && this.isOpen) this.close(false);
      }, options);

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', (event) => this.handleBlockSelect(event), options);
      }
    }

    initPosterObserver() {
      const posters = Array.from(this.querySelectorAll('img[data-smg-poster]'));
      if (!posters.length) return;

      if (!('IntersectionObserver' in window)) {
        posters.forEach((poster) => this.loadPoster(poster));
        return;
      }

      this.posterObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.loadPoster(entry.target);
          this.posterObserver?.unobserve(entry.target);
        });
      }, { rootMargin: '500px 20%', threshold: 0 });

      posters.forEach((poster) => this.posterObserver.observe(poster));
    }

    initRailResizeObserver() {
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleRailUpdate());
        this.resizeObserver.observe(this.rail);
      }
      this.scheduleRailUpdate();
    }

    scheduleRailUpdate() {
      if (this.scrollFrame) return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = 0;
        this.updateRailButtons();
      });
    }

    loadPoster(image) {
      if (!image || image.dataset.smgLoaded === 'true') return;
      if (image.dataset.srcset) image.srcset = image.dataset.srcset.replace(/\s+/g, ' ').trim();
      if (image.dataset.sizes) image.sizes = image.dataset.sizes;
      if (image.dataset.src) image.src = image.dataset.src;
      image.dataset.smgLoaded = 'true';
    }

    getScrollDistance() {
      const card = this.cards[0];
      if (!card) return this.rail.clientWidth * 0.8;
      const style = window.getComputedStyle(this.rail);
      const gap = parseFloat(style.columnGap || style.gap) || 0;
      return card.getBoundingClientRect().width + gap;
    }

    scrollRail(direction) {
      if (!this.cards.length) return;
      const maxScroll = Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
      const atStart = this.rail.scrollLeft <= 4;
      const atEnd = this.rail.scrollLeft >= maxScroll - 4;
      const behavior = reducedMotion.matches ? 'auto' : 'smooth';

      if (this.loop && direction < 0 && atStart) {
        this.rail.scrollTo({ left: maxScroll, behavior });
      } else if (this.loop && direction > 0 && atEnd) {
        this.rail.scrollTo({ left: 0, behavior });
      } else {
        this.rail.scrollBy({ left: direction * this.getScrollDistance(), behavior });
      }
    }

    updateRailButtons() {
      if (!this.railPrev || !this.railNext) return;
      const maxScroll = Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
      const noOverflow = maxScroll <= 4;
      const atStart = this.rail.scrollLeft <= 4;
      const atEnd = this.rail.scrollLeft >= maxScroll - 4;

      this.railPrev.disabled = noOverflow || (!this.loop && atStart);
      this.railNext.disabled = noOverflow || (!this.loop && atEnd);
    }

    handleRailKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const openButtons = this.cards.map((card) => card.querySelector('[data-smg-open]')).filter(Boolean);
      const activeIndex = openButtons.indexOf(document.activeElement);

      event.preventDefault();
      if (activeIndex < 0) {
        this.scrollRail(direction);
        return;
      }

      let nextIndex = activeIndex + direction;
      if (this.loop) nextIndex = (nextIndex + openButtons.length) % openButtons.length;
      else nextIndex = Math.max(0, Math.min(openButtons.length - 1, nextIndex));

      openButtons[nextIndex].focus();
      openButtons[nextIndex].scrollIntoView({
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }

    startRailDrag(event) {
      if (event.pointerType === 'touch' || event.pointerType === 'pen') {
        if (event.isPrimary === false) {
          this.touchTapState = null;
          return;
        }
        const opener = event.target.closest('[data-smg-open]');
        const card = opener?.closest('[data-smg-card]');
        const index = card ? this.cards.indexOf(card) : -1;
        if (!opener || index < 0) return;
        this.touchTapState = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          moved: false,
          opener,
          index,
        };
        return;
      }

      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (event.target.closest('a')) return;
      this.dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScroll: this.rail.scrollLeft,
        moved: false,
      };
    }

    moveRailDrag(event) {
      if (this.touchTapState && event.pointerId === this.touchTapState.pointerId) {
        const deltaX = event.clientX - this.touchTapState.startX;
        const deltaY = event.clientY - this.touchTapState.startY;
        if (Math.hypot(deltaX, deltaY) > 10) this.touchTapState.moved = true;
        return;
      }

      if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;
      if ((event.buttons & 1) !== 1) return;
      const delta = event.clientX - this.dragState.startX;
      if (Math.abs(delta) > 5 && !this.dragState.moved) {
        this.dragState.moved = true;
        this.rail.classList.add('is-dragging');
        this.rail.setPointerCapture?.(event.pointerId);
      }
      if (!this.dragState.moved) return;
      event.preventDefault();
      this.rail.scrollLeft = this.dragState.startScroll - delta;
    }

    endRailDrag(event) {
      if (this.touchTapState && event.pointerId === this.touchTapState.pointerId) {
        const tap = this.touchTapState;
        this.touchTapState = null;
        const wasCancelled =
          event.type === 'pointercancel' ||
          event.type === 'lostpointercapture';
        if (!wasCancelled && tap.moved) {
          this.suppressCardClick(450);
        } else if (!wasCancelled) {
          this.queueTouchOpen(tap);
        }
        return;
      }

      if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;
      const moved = this.dragState.moved;
      try {
        this.rail.releasePointerCapture?.(event.pointerId);
      } catch (_error) {
        // Pointer capture may already be released by the browser.
      }
      this.dragState = null;
      this.rail.classList.remove('is-dragging');
      if (moved) {
        this.suppressCardClick(0);
      }
    }

    suppressCardClick(duration = 0) {
      this.clearPendingTouchOpen();
      this.suppressNextClick = true;
      if (this.suppressClickTimer) window.clearTimeout(this.suppressClickTimer);
      this.suppressClickTimer = window.setTimeout(() => {
        this.suppressNextClick = false;
        this.suppressClickTimer = 0;
      }, duration);
    }

    queueTouchOpen(tap) {
      this.clearPendingTouchOpen();
      this.pendingTouchTap = tap;
      this.touchOpenTimer = window.setTimeout(() => {
        const pendingTap = this.pendingTouchTap;
        this.pendingTouchTap = null;
        this.touchOpenTimer = 0;
        if (pendingTap && pendingTap.opener?.isConnected !== false) {
          this.open(pendingTap.index, pendingTap.opener);
        }
      }, 160);
    }

    clearPendingTouchOpen() {
      if (this.touchOpenTimer) window.clearTimeout(this.touchOpenTimer);
      this.touchOpenTimer = 0;
      this.pendingTouchTap = null;
    }

    startRailTouch(event) {
      if (event.touches.length !== 1) {
        this.touchTapState = null;
        return;
      }
      const touch = event.touches[0];
      const opener = event.target.closest('[data-smg-open]');
      const card = opener?.closest('[data-smg-card]');
      const index = card ? this.cards.indexOf(card) : -1;
      if (!opener || index < 0) return;
      this.touchTapState = {
        pointerId: touch.identifier,
        startX: touch.clientX,
        startY: touch.clientY,
        moved: false,
        opener,
        index,
      };
    }

    moveRailTouch(event) {
      if (!this.touchTapState) return;
      const touch = Array.from(event.touches).find(
        (candidate) => candidate.identifier === this.touchTapState.pointerId
      );
      if (!touch) return;
      const deltaX = touch.clientX - this.touchTapState.startX;
      const deltaY = touch.clientY - this.touchTapState.startY;
      if (Math.hypot(deltaX, deltaY) > 10) this.touchTapState.moved = true;
    }

    endRailTouch(event) {
      if (!this.touchTapState) return;
      const touch = Array.from(event.changedTouches).find(
        (candidate) => candidate.identifier === this.touchTapState.pointerId
      );
      if (!touch) return;
      const tap = this.touchTapState;
      this.touchTapState = null;
      if (tap.moved) this.suppressCardClick(450);
      else this.queueTouchOpen(tap);
    }

    open(index, opener) {
      if (!this.cards.length) return;

      document.dispatchEvent(new CustomEvent('ohcrisp:gallery-opening', {
        detail: { gallery: this },
      }));

      const headerMenuButton = document.querySelector('[data-ohc-menu-button][aria-expanded="true"]');
      if (headerMenuButton) headerMenuButton.click();

      const cartDrawer = document.querySelector('cart-drawer.active');
      if (cartDrawer && typeof cartDrawer.close === 'function') cartDrawer.close();

      this.returnFocus = opener || this.cards[index]?.querySelector('[data-smg-open]') || null;
      this.restoreFocusOnClose = true;
      this.isOpen = true;
      openGalleries.add(this);
      document.documentElement.classList.add('smg-modal-open');

      this.renderItem(index);
      this.openDialog();

      window.requestAnimationFrame(() => this.closeButton?.focus());
    }

    openDialog() {
      if (!this.dialog) return;

      if (this.shouldUseDialogFallback() || typeof this.dialog.showModal !== 'function') {
        this.showDialogFallback();
        return;
      }

      if (this.dialog.open) return;
      try {
        this.dialog.showModal();
      } catch (_error) {
        this.showDialogFallback();
      }
    }

    shouldUseDialogFallback() {
      const userAgent = navigator.userAgent || '';
      const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent);
      const isTouchMac = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
      return isIOSDevice || isTouchMac;
    }

    close(restoreFocus = true) {
      if (!this.isOpen && !this.dialog?.open) return;
      this.restoreFocusOnClose = restoreFocus;
      this.stopActiveVideo();

      if (typeof this.dialog.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.finalizeClose();
      }
    }

    finalizeClose() {
      if (!this.isOpen && !openGalleries.has(this)) return;
      this.isOpen = false;
      this.stopActiveVideo();
      this.hideDialogFallback();
      this.mediaStage?.replaceChildren();
      if (this.productController) this.productController.abort();
      this.productController = null;
      this.productMount?.replaceChildren();
      openGalleries.delete(this);
      this.releaseBodyLock();

      if (this.restoreFocusOnClose && this.returnFocus?.isConnected) {
        this.returnFocus.focus();
      }
    }

    showDialogFallback() {
      if (!this.dialog) return;
      this.fallbackSection = this.closest('.shopify-section');
      this.fallbackSection?.classList.add('smg-fallback-active');
      this.dialog.setAttribute('open', '');
      this.dialog.classList.add('smg__dialog--fallback');
      if (this.fallbackBackdrop) {
        this.fallbackBackdrop.hidden = false;
        this.fallbackBackdrop.classList.add('is-active');
      }
    }

    hideDialogFallback() {
      this.dialog?.classList.remove('smg__dialog--fallback');
      this.fallbackSection?.classList.remove('smg-fallback-active');
      this.fallbackSection = null;
      if (this.fallbackBackdrop) {
        this.fallbackBackdrop.classList.remove('is-active');
        this.fallbackBackdrop.hidden = true;
      }
    }

    releaseBodyLock() {
      openGalleries.delete(this);
      if (!openGalleries.size) document.documentElement.classList.remove('smg-modal-open');
    }

    normalizeIndex(index) {
      if (!this.cards.length) return 0;
      if (this.loop) return (index + this.cards.length) % this.cards.length;
      return Math.max(0, Math.min(this.cards.length - 1, index));
    }

    renderItem(index) {
      const nextIndex = this.normalizeIndex(index);
      const card = this.cards[nextIndex];
      if (!card) return;

      this.currentIndex = nextIndex;
      const activeElement = document.activeElement;
      const dynamicFocusWillBeRemoved =
        activeElement &&
        (this.mediaStage?.contains(activeElement) || this.productMount?.contains(activeElement));
      this.stopActiveVideo();
      if (this.productController) this.productController.abort();
      this.productController = null;
      this.mediaStage.replaceChildren();
      this.productMount.replaceChildren();

      const mediaTemplate = card.querySelector('[data-smg-media-template]');
      if (mediaTemplate) this.mediaStage.append(mediaTemplate.content.cloneNode(true));

      const video = this.mediaStage.querySelector('[data-smg-video]');
      if (video) {
        this.activeVideo = video;
        video.muted = true;
        const videoFrame = video.closest('[data-smg-video-frame]');
        video.addEventListener('play', () => this.updateVideoControls());
        video.addEventListener('playing', () => {
          videoFrame?.classList.add('is-playing');
          this.updateVideoControls();
        });
        video.addEventListener('pause', () => this.updateVideoControls());
        video.addEventListener('volumechange', () => this.updateVideoControls());
        video.addEventListener('ended', () => {
          video.currentTime = 0;
          videoFrame?.classList.remove('is-playing');
          this.updateVideoControls();
        });
        video.addEventListener('error', () => {
          videoFrame?.classList.remove('is-playing');
          if (this.announcer) this.announcer.textContent = 'Video could not be played.';
          this.updateVideoControls();
        });
        video.addEventListener('click', () => this.togglePlay());
      }
      this.updateVideoControls();

      this.setText(this.badge, card.dataset.smgBadge);
      this.setText(this.creator, card.dataset.smgCreator);
      this.setText(this.caption, card.dataset.smgCaption);
      if (this.originalLink) {
        const originalUrl = card.dataset.smgOriginalUrl || '';
        this.originalLink.hidden = !originalUrl;
        if (originalUrl) this.originalLink.href = originalUrl;
        else this.originalLink.removeAttribute('href');
      }

      const productTemplate = card.querySelector('[data-smg-product-template]');
      if (productTemplate) {
        this.productMount.append(productTemplate.content.cloneNode(true));
        this.initProductPanel();
      }

      const itemMessage = `Item ${nextIndex + 1} of ${this.cards.length}`;
      if (this.counter) this.counter.textContent = itemMessage;
      if (this.announcer) this.announcer.textContent = itemMessage;
      this.updateModalButtons();
      if (dynamicFocusWillBeRemoved) {
        window.requestAnimationFrame(() => {
          if (!this.isOpen || this.dialog.contains(document.activeElement)) return;
          this.closeButton?.focus({ preventScroll: true });
        });
      }
    }

    setText(element, value) {
      if (!element) return;
      element.textContent = value || '';
      element.hidden = !value;
    }

    showPrevious() {
      if (!this.loop && this.currentIndex === 0) return;
      this.renderItem(this.currentIndex - 1);
    }

    showNext() {
      if (!this.loop && this.currentIndex === this.cards.length - 1) return;
      this.renderItem(this.currentIndex + 1);
    }

    updateModalButtons() {
      if (!this.modalPrev || !this.modalNext) return;
      const single = this.cards.length <= 1;
      this.modalPrev.disabled = single || (!this.loop && this.currentIndex === 0);
      this.modalNext.disabled = single || (!this.loop && this.currentIndex === this.cards.length - 1);
    }

    startViewerSwipe(event) {
      if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
      if (event.target.closest('button, a, select, input')) return;
      this.swipeState = {
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
      };
    }

    endViewerSwipe(event) {
      if (!this.swipeState || event.pointerId !== this.swipeState.pointerId) return;
      const deltaX = event.clientX - this.swipeState.x;
      const deltaY = event.clientY - this.swipeState.y;
      this.swipeState = null;
      if (Math.abs(deltaX) < 50 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;
      if (deltaX > 0) this.showPrevious();
      else this.showNext();
    }

    handleDialogKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
        return;
      }

      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        if (event.target.matches('input, select, textarea')) return;
        event.preventDefault();
        if (event.key === 'ArrowLeft') this.showPrevious();
        else this.showNext();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = Array.from(this.dialog.querySelectorAll(focusableSelector)).filter((element) => {
        return !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.getClientRects().length;
      });
      if (!focusable.length) {
        event.preventDefault();
        this.dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    togglePlay() {
      const video = this.activeVideo;
      if (!video) return;
      if (video.paused) {
        this.hydrateVideo(video);
        video.play().catch(() => {
          if (this.announcer) this.announcer.textContent = 'Video could not be played.';
        });
      } else {
        video.pause();
      }
    }

    hydrateVideo(video) {
      if (!video || video.dataset.smgHydrated === 'true') return;
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
      });
      video.dataset.smgHydrated = 'true';
      video.load();
    }

    toggleMute() {
      if (!this.activeVideo) return;
      this.activeVideo.muted = !this.activeVideo.muted;
      this.updateVideoControls();
    }

    updateVideoControls() {
      const hasVideo = Boolean(this.activeVideo);
      if (this.playButton) this.playButton.hidden = !hasVideo || !this.activeVideo.paused;
      if (this.videoControls) this.videoControls.hidden = !hasVideo;
      if (!hasVideo) return;

      const isPaused = this.activeVideo.paused;
      if (this.playToggle) {
        this.playToggle.textContent = isPaused ? 'Play' : 'Pause';
        this.playToggle.setAttribute('aria-label', isPaused ? 'Play video' : 'Pause video');
        this.playToggle.setAttribute('aria-pressed', isPaused ? 'false' : 'true');
      }
      if (this.muteToggle) {
        this.muteToggle.textContent = this.activeVideo.muted ? 'Unmute' : 'Mute';
        this.muteToggle.setAttribute('aria-label', this.activeVideo.muted ? 'Unmute video' : 'Mute video');
        this.muteToggle.setAttribute('aria-pressed', this.activeVideo.muted ? 'false' : 'true');
      }
      if (this.playButton) this.playButton.setAttribute('aria-label', isPaused ? 'Play video' : 'Pause video');
    }

    stopActiveVideo() {
      if (!this.activeVideo) {
        this.updateVideoControls();
        return;
      }

      try {
        this.activeVideo.pause();
        this.activeVideo.currentTime = 0;
      } catch (_error) {
        // A source may not have loaded far enough to seek.
      }
      this.activeVideo.removeAttribute('src');
      this.activeVideo.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
      this.activeVideo.load();
      this.activeVideo = null;
      this.updateVideoControls();
    }

    initProductPanel() {
      const form = this.productMount.querySelector('[data-smg-product-form]');
      if (!form) return;
      if (this.productController) this.productController.abort();
      this.productController = new AbortController();
      const productSignal = this.productController.signal;
      const variantControl = form.querySelector('[data-smg-variant]');

      if (variantControl?.tagName === 'SELECT') {
        variantControl.addEventListener('change', () => this.updateVariant(form), { signal: productSignal });
      }
      form.addEventListener('submit', (event) => this.submitProduct(event, form), { signal: productSignal });
      this.updateVariant(form);
    }

    getVariantState(form) {
      const control = form.querySelector('[data-smg-variant]');
      if (!control) return null;
      const option = control.tagName === 'SELECT' ? control.options[control.selectedIndex] : control;
      if (!option) return null;
      return {
        id: control.value,
        available: option.dataset.available !== 'false',
        price: option.dataset.price || '',
        compare: option.dataset.compare || '',
        image: option.dataset.image || '',
        quantityMin: Math.max(1, Number.parseInt(option.dataset.quantityMin || '1', 10) || 1),
        quantityMax: Number.parseInt(option.dataset.quantityMax || '', 10) || null,
        quantityIncrement: Math.max(1, Number.parseInt(option.dataset.quantityIncrement || '1', 10) || 1),
      };
    }

    updateVariant(form) {
      const state = this.getVariantState(form);
      if (!state) return;
      const product = form.closest('[data-smg-product]');
      const price = product?.querySelector('[data-smg-price]');
      const compare = product?.querySelector('[data-smg-compare-price]');
      const availability = product?.querySelector('[data-smg-availability]');
      const image = product?.querySelector('.smg__product-image');
      const viewProduct = product?.querySelector('[data-smg-view-product]');
      const quantity = form.querySelector('input[name="quantity"]');
      const button = form.querySelector('[data-smg-add]');

      if (price && state.price) price.textContent = state.price;
      if (compare) {
        compare.textContent = state.compare;
        compare.hidden = !state.compare;
      }
      if (availability) {
        availability.textContent = state.available ? 'In stock' : 'Sold out';
        availability.dataset.soldOut = state.available ? 'false' : 'true';
      }
      if (image && state.image) {
        image.src = state.image;
        image.removeAttribute('srcset');
      }
      if (viewProduct) {
        const baseUrl = viewProduct.dataset.baseUrl || viewProduct.getAttribute('href') || '';
        if (baseUrl && state.id) {
          const separator = baseUrl.includes('?') ? '&' : '?';
          viewProduct.href = `${baseUrl}${separator}variant=${encodeURIComponent(state.id)}`;
        }
      }
      if (quantity) {
        quantity.min = String(state.quantityMin);
        quantity.step = String(state.quantityIncrement);
        if (state.quantityMax) quantity.max = String(state.quantityMax);
        else quantity.removeAttribute('max');
        const currentQuantity = Number.parseInt(quantity.value || '', 10);
        let normalizedQuantity = currentQuantity || state.quantityMin;
        normalizedQuantity = Math.max(state.quantityMin, normalizedQuantity);
        if (state.quantityMax) normalizedQuantity = Math.min(state.quantityMax, normalizedQuantity);
        const completedSteps = Math.floor(
          (normalizedQuantity - state.quantityMin) / state.quantityIncrement
        );
        quantity.value = String(
          state.quantityMin + completedSteps * state.quantityIncrement
        );
      }
      if (button) {
        if (form.dataset.smgSubmitting === 'true') return;
        button.disabled = !state.available;
        button.setAttribute('aria-disabled', state.available ? 'false' : 'true');
        button.textContent = state.available ? button.dataset.defaultLabel || 'Add to Cart' : 'Sold Out';
        button.classList.remove('is-adding', 'is-added', 'is-error');
      }
    }

    async submitProduct(event, form) {
      event.preventDefault();
      if (form.dataset.smgSubmitting === 'true') return;

      const button = form.querySelector('[data-smg-add]');
      const status = form.querySelector('[data-smg-form-status]');
      const variant = this.getVariantState(form);
      if (!button || !variant || !variant.available || button.disabled) return;
      const formData = new FormData(form);

      form.dataset.smgSubmitting = 'true';
      const formControls = Array.from(form.querySelectorAll('select, input'));
      formControls.forEach((control) => {
        control.disabled = true;
      });
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.classList.remove('is-added', 'is-error');
      button.classList.add('is-adding');
      button.textContent = 'Adding…';
      if (status) {
        status.textContent = 'Adding item to cart.';
        status.setAttribute('aria-live', 'polite');
      }

      const cartDrawer = document.querySelector('cart-drawer');
      if (
        cartDrawer &&
        typeof cartDrawer.getSectionsToRender === 'function'
      ) {
        formData.set(
          'sections',
          cartDrawer.getSectionsToRender().map((section) => section.id).join(',')
        );
        formData.set('sections_url', window.location.pathname);
      }

      const cartAddUrl =
        window.routes?.cart_add_url ||
        `${window.Shopify?.routes?.root || '/'}cart/add.js`;

      try {
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });

        let state = null;
        try {
          state = await response.json();
        } catch (_error) {
          // The generic error below handles non-JSON responses.
        }

        if (!response.ok || !state || state.status) {
          const message =
            typeof state?.description === 'string' && state.description.length < 240
              ? state.description
              : 'This item could not be added. Please try again.';
          throw new Error(message);
        }

        button.classList.remove('is-adding');
        button.classList.add('is-added');
        button.textContent = 'Added';
        if (status) status.textContent = 'Added to cart.';

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          try {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'shoppable-media-gallery',
              productVariantId: variant.id,
              cartData: state,
            });
          } catch (publishError) {
            console.error('[OhCrisp shoppable gallery] Cart updated, but the cart event could not be published.', publishError);
          }
        }

        if (
          cartDrawer &&
          typeof cartDrawer.renderContents === 'function'
        ) {
          try {
            if (typeof cartDrawer.setActiveElement === 'function') {
              cartDrawer.setActiveElement(this.returnFocus?.isConnected ? this.returnFocus : button);
            }
            this.close(false);
            // Match Dawn's native product form: a drawer that started empty
            // must leave empty mode before its freshly rendered item can show.
            cartDrawer.classList.remove('is-empty');
            cartDrawer.renderContents(state);
          } catch (drawerError) {
            console.error('[OhCrisp shoppable gallery] Cart updated, but the drawer could not open.', drawerError);
            if (status?.isConnected) {
              status.textContent = 'Added to cart. Open the cart to review it.';
            }
          }
        } else {
          window.setTimeout(() => {
            if (!button.isConnected) return;
            button.classList.remove('is-added');
            button.textContent = button.dataset.defaultLabel || 'Add to Cart';
            formControls.forEach((control) => {
              control.disabled = false;
            });
            this.updateVariant(form);
          }, 1400);
        }
      } catch (error) {
        const message = error.message || 'This item could not be added. Please try again.';
        button.disabled = false;
        button.setAttribute('aria-disabled', 'false');
        button.classList.remove('is-adding');
        button.classList.add('is-error');
        button.textContent = 'Error — Try Again';
        if (status) {
          status.textContent = message;
          status.setAttribute('aria-live', 'assertive');
        }
        console.error('[OhCrisp shoppable gallery]', error);
      } finally {
        delete form.dataset.smgSubmitting;
        formControls.forEach((control) => {
          control.disabled = false;
        });
      }
    }

    handleBlockSelect(event) {
      const blockId = event.detail?.blockId;
      if (!blockId) return;
      const escapedBlockId =
        window.CSS && typeof window.CSS.escape === 'function'
          ? window.CSS.escape(blockId)
          : String(blockId).replace(/["\\]/g, '\\$&');
      const card = this.querySelector(`[data-smg-block-id="${escapedBlockId}"]`);
      if (!card) return;
      card.scrollIntoView({
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
        block: 'center',
        inline: 'center',
      });
    }
  }

  customElements.define('ohcrisp-shoppable-gallery', OhCrispShoppableGallery);
})();
